import * as Apollo from 'apollo-angular'
import { QueryOptionsAlone } from 'apollo-angular'
import {
  ApolloQueryResult,
  DocumentNode,
  OperationVariables,
} from '@apollo/client'
import { defer, Observable } from 'rxjs'
import {
  splitParallelDocument,
  SplitParallelDocumentResult,
} from './split-parallel-document'

export interface ParallelFetch<Query> {
  /**
   * Main query result stream. Subscribing fires the main query and every
   * deferred companion in parallel.
   */
  main$: Observable<ApolloQueryResult<Query>>
  /**
   * The caller must invoke this right after applying the main response to
   * its store: deferred partials that won the race (buffered until then,
   * since there is nothing to merge into before the main rows exist) are
   * flushed to `applyPartials`, and later ones are applied directly.
   */
  notifyMainApplied: () => void
}

/**
 * Runs a GQL query whose document may carry `... @parallel` annotations:
 * the split main and deferred documents are fired in parallel, and deferred
 * results are handed to `applyPartials` only when it is safe to merge them
 * (main response applied, fetch still current). Deferred failures are
 * non-fatal: the main data stays up with the deferred fields left empty.
 *
 * Documents without `@parallel` — or runners without an Apollo handle, as
 * with mocked GQL services in specs — behave exactly like `gqlQuery.fetch()`.
 *
 * One runner per state: it tracks a fetch generation so a slow deferred
 * response from a superseded fetch (filters/pagination/entity changed
 * mid-flight) is discarded instead of merged.
 */
export class ParallelQueryRunner<
  Query,
  Body extends OperationVariables,
  PartialEntity
> {
  private _generation = 0

  constructor(
    private readonly _gqlQuery: Apollo.Query<Query, Body>,
    private readonly _apollo: Apollo.Apollo | null
  ) {}

  public fetch(
    body: Body,
    context: QueryOptionsAlone<any> | undefined,
    mapPartials: (source: Query) => PartialEntity[],
    applyPartials: (partials: PartialEntity[]) => void
  ): ParallelFetch<Query> {
    const generation = ++this._generation
    const split = this._splitDocument()
    let mainApplied = false
    const buffered: PartialEntity[][] = []
    const apply = (partials: PartialEntity[]): void => {
      if (generation !== this._generation) {
        return
      }
      if (!mainApplied) {
        buffered.push(partials)
        return
      }
      applyPartials(partials)
    }
    const notifyMainApplied = (): void => {
      mainApplied = true
      if (generation === this._generation) {
        buffered.splice(0).forEach((partials) => applyPartials(partials))
      }
    }
    if (!split.deferred.length) {
      return { main$: this._gqlQuery.fetch(body, context), notifyMainApplied }
    }
    // All queries fire in parallel when the caller subscribes: the deferred
    // subscriptions (one per @parallel group, each merging independently)
    // are kicked here, the main one by the caller
    const main$ = defer(() => {
      split.deferred.forEach((deferredDocument) =>
        this._fetchDeferred(deferredDocument, body, context, mapPartials, apply)
      )
      return this._query(split.main, body, context)
    })
    return { main$, notifyMainApplied }
  }

  /**
   * Splitting requires a direct Apollo handle (the two docs differ from the
   * service's own document); without one — or without a document, as with
   * mocked GQL services in specs — the runner degrades to a plain fetch
   */
  private _splitDocument(): SplitParallelDocumentResult {
    const document = this._gqlQuery.document
    if (!document || !this._apollo) {
      return { main: document, deferred: [] }
    }
    return splitParallelDocument(document)
  }

  private _query(
    query: DocumentNode,
    variables: Body,
    context?: QueryOptionsAlone<any>
  ): Observable<ApolloQueryResult<Query>> {
    const client =
      this._gqlQuery.client && this._gqlQuery.client !== 'default'
        ? (this._apollo as Apollo.Apollo).use(this._gqlQuery.client)
        : (this._apollo as Apollo.Apollo)
    return client.query<Query>({ ...(context ?? {}), query, variables })
  }

  private _fetchDeferred(
    query: DocumentNode,
    body: Body,
    context: QueryOptionsAlone<any> | undefined,
    mapPartials: (source: Query) => PartialEntity[],
    apply: (partials: PartialEntity[]) => void
  ): void {
    this._query(query, body, {
      fetchPolicy: 'no-cache',
      ...(context ?? {}),
    }).subscribe({
      next: (res) => apply(mapPartials(res.data)),
      error: (error) =>
        console.warn(
          '[parallel] deferred query failed, deferred fields stay empty',
          error
        ),
    })
  }
}
